<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhxWatchComparatorPill } from '@phx-styleguides/jlc';
import { emitter } from '@phoenix/event-bus';
import { showPanel } from '@phoenix/helpers/panel-helper';
import { WATCH_COMPARATOR_UPDATED } from '@maison/helpers/watch-comparator-localstorage-helper';
import { useWatchComparator } from '@maison/composables/watchComparator';

const POPOVER_TIMEOUT = 5000;

defineProps({
  placement: {
    type: String,
    default: 'bottom-end',
  },
  offset: {
    type: Object,
    default: () => ({ mainAxis: 15, crossAxis: 10 }),
  },
});

const { t } = useI18n();
const { count } = useWatchComparator();

const isPopoverOpen = ref(false);
let timer = null;

const triggerAttrs = computed(() => ({
  'aria-label': t('WatchComparator.pill.ariaLabel'),
  onClick: () => {
    showPanel('watch-comparator-panel');
  },
}));

const notifyAdded = async () => {
  clearTimeout(timer);

  if (isPopoverOpen.value) {
    isPopoverOpen.value = false;
    await nextTick();
  }

  isPopoverOpen.value = true;
  timer = setTimeout(() => {
    isPopoverOpen.value = false;
  }, POPOVER_TIMEOUT);
};

const onUpdate = (payload) => {
  if (payload?.added) {
    notifyAdded();
  }
};

onMounted(() => emitter.on(WATCH_COMPARATOR_UPDATED, onUpdate));

onBeforeUnmount(() => {
  clearTimeout(timer);
  emitter.off(WATCH_COMPARATOR_UPDATED, onUpdate);
});
</script>

<template>
  <phx-watch-comparator-pill
    :value="count || null"
    :is-popover-open="isPopoverOpen"
    :placement="placement"
    :offset="offset"
    :trigger-attrs="triggerAttrs"
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>

    <template #popover-content>
      <div class="flex flex-col items-start gap-2xs text-sm p-sm" data-cy="watch-comparator-success">
        <div class="uppercase font-secondary font-semibold">{{ $t('WatchComparator.toaster.success.title') }}</div>

        <p>{{ $t('WatchComparator.toaster.success.description') }}</p>
      </div>
    </template>
  </phx-watch-comparator-pill>
</template>
